import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { GridColDef, GridValueFormatterParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import {
  ServerDataGrid,
  currencyFormatter,
  dateFormatter,
  qtyFormatter,
  usePagination,
  useSorting,
} from "components/ui/ServerDataGrid";
import { Dayjs } from "dayjs";
import { useListTransactionsQuery } from "features/trading/accounts";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

// The three columns the API accepts an ordering term for. Every other header
// stays inert: an unrecognised ordering term is dropped rather than rejected,
// so a movable header over unchanged rows reads as broken rather than as
// unsupported -- the lesson OrdersList records.
const SORTABLE_FIELDS = new Set([
  "transaction_date",
  "ticker",
  "account_number",
]);

interface TransactionTypeCategory {
  value: string;
  label: string;
}

// Categories rather than Orion's raw type names: Orion sends ~40 of those and
// a user asks for "Deposits", which is nine of them. The values are the API's
// own -- their single home is TRANSACTION_TYPE_CATEGORY_IDS in the backend's
// services/transaction_utils.py, which derives each set from the same enums the
// report calculations use.
//
// All seven are always offered, including any with no rows: the six are a fixed
// product concept, so the page can list them itself and costs no extra request
// to build the dropdown. A category matching nothing renders as the grid's own
// "No rows" rather than as a missing option.
//
// This list is a hand-kept copy across the language boundary, which is how this
// app carries such a list (see PDF_REPORT_PLACEHOLDER_VALUES). Two tests keep
// the halves in step, one per direction, because the API refuses a value it
// does not recognise -- a typo here is not a wide result set but a checkbox
// that answers 400 and empties the grid. The backend names these seven values
// as literals, and this file's own test sends each label and asserts the value
// it emits.
const TRANSACTION_TYPE_CATEGORIES: TransactionTypeCategory[] = [
  { value: "buys", label: "Buys" },
  { value: "sells", label: "Sells" },
  { value: "deposits", label: "Deposits" },
  { value: "withdrawals", label: "Withdrawals" },
  { value: "dividends", label: "Dividends" },
  { value: "margin_interest", label: "Margin Interest" },
  // Every type id in none of the six -- internal journals and the rest. Without
  // it those rows would be visible only with the filter cleared.
  { value: "other", label: "Other" },
];

const API_DATE_FORMAT = "YYYY-MM-DD";

const DISPLAY_DATE_FORMAT = "MM/DD/YYYY";

// Matches the debounce every other search box in this app settles on, so a
// keystroke does not cost a request.
const FILTER_DEBOUNCE_MS = 250;

// Both date columns are nullable, and dateFormatter hands its value straight to
// dayjs -- a null renders as "Invalid Date". N/A is what an absent value reads
// as everywhere else on this page.
const nullableDateFormatter = (params: any) =>
  params.value ? dateFormatter(params) : <span>N/A</span>;

// Column widths are explicit because the sort arrow and the column menu take
// ~68px out of a header: at the 100px default "Transaction Date" clips to
// "Tran..." and "Status" to "St...". Sortable columns get at least 150.
export const COLUMN_DEFS: GridColDef[] = [
  {
    field: "transaction_date",
    headerName: "Transaction Date",
    width: 200,
    renderCell: nullableDateFormatter,
  },
  {
    field: "settlement_date",
    headerName: "Settlement Date",
    width: 180,
    renderCell: nullableDateFormatter,
  },
  {
    field: "account_number",
    headerName: "Account #",
    width: 160,
  },
  {
    field: "ticker",
    headerName: "Symbol",
    // Option symbols are OCC-formatted ("SPY 260702C00746000"), so the 150 the
    // header needs clipped the strike off ~10k rows -- the part of the symbol
    // that identifies the contract. 200 fits those; only the rare 29-character
    // outlier still truncates.
    width: 200,
  },
  {
    field: "transaction_type",
    headerName: "Transaction Type",
    // 240 rather than the 200 the header alone needs: the longest type Orion
    // sends is "Qualified Dividend to Cash", which clipped to "Qualified
    // Dividend to C..." on every one of those rows.
    width: 240,
  },
  {
    field: "units_amount",
    headerName: "Units",
    width: 130,
    align: "right",
    headerAlign: "right",
    renderCell: qtyFormatter,
  },
  {
    field: "nav_price",
    headerName: "NAV Price",
    width: 150,
    align: "right",
    headerAlign: "right",
    renderCell: currencyFormatter,
  },
  {
    field: "transaction_amount",
    headerName: "Transaction Amount",
    width: 210,
    align: "right",
    headerAlign: "right",
    renderCell: currencyFormatter,
  },
  {
    field: "unit_balance",
    headerName: "Unit Balance",
    width: 170,
    align: "right",
    headerAlign: "right",
    renderCell: qtyFormatter,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
  },
];

const COLUMNS: GridColDef[] = COLUMN_DEFS.map((col) => ({
  ...col,
  sortable: SORTABLE_FIELDS.has(col.field),
  valueFormatter: ({ value }: GridValueFormatterParams) =>
    value === null || value === undefined ? "N/A" : value,
}));

export const TransactionsList = () => {
  const [pagination, setPagination] = usePagination();
  // Newest first: the API's own ordering default, and what a transaction
  // blotter is read for -- so the header arrow states what the rows do.
  const [sorting, setSorting] = useSorting("transaction_date", "desc");

  const [accountNumber, setAccountNumber] = useState("");
  const [symbol, setSymbol] = useState("");
  const [typeCategories, setTypeCategories] = useState<
    TransactionTypeCategory[]
  >([]);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  // One date input standing in for both ends of the range, rather than a second
  // filter system beside it: the switch collapses the two pickers into one and
  // every pick writes both ends.
  const [singleDay, setSingleDay] = useState(false);

  const [debouncedAccountNumber] = useDebounce(
    accountNumber,
    FILTER_DEBOUNCE_MS,
  );
  const [debouncedSymbol] = useDebounce(symbol, FILTER_DEBOUNCE_MS);

  const filters = useMemo(() => {
    const query: Record<string, string> = {};
    const account = debouncedAccountNumber.trim();
    // Tickers are stored upper case and the match is partial, so "aapl" would
    // otherwise come back empty and read as no data rather than as a typo.
    const ticker = debouncedSymbol.trim().toUpperCase();

    if (account) {
      query.account_number = account;
    }
    if (ticker) {
      query.ticker = ticker;
    }
    if (typeCategories.length) {
      // Comma-separated, which is what the request would carry regardless:
      // RTK Query hands its params to URLSearchParams, and that joins a list
      // on commas. Sending the string makes the wire format the API parses
      // explicit rather than incidental.
      query.transaction_type_category = typeCategories
        .map((category) => category.value)
        .join(",");
    }
    // A half-typed date is a valid Dayjs object holding an invalid date;
    // formatting it would send the literal "Invalid Date" as a filter.
    if (dateFrom?.isValid()) {
      query.transaction_date_after = dateFrom.format(API_DATE_FORMAT);
    }
    if (dateTo?.isValid()) {
      query.transaction_date_before = dateTo.format(API_DATE_FORMAT);
    }

    return query;
  }, [
    debouncedAccountNumber,
    debouncedSymbol,
    typeCategories,
    dateFrom,
    dateTo,
  ]);

  // Serialised so the reset below keys on what the request actually carries
  // rather than on a fresh object identity every render.
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    // A narrowed result set makes the current page index meaningless --
    // requesting page 50 of a two-page response returns nothing.
    setPagination((current) =>
      current.page === 0 ? current : { ...current, page: 0 },
    );
  }, [filterKey, setPagination]);

  const { data, isFetching } = useListTransactionsQuery({
    pagination,
    sorting,
    filters,
  });

  const handleSingleDayToggle = (enabled: boolean) => {
    setSingleDay(enabled);

    if (!enabled) {
      return;
    }
    // Snap both ends onto one day so the two modes can never disagree about
    // what is filtered. Whichever end is already set wins; an empty range stays
    // empty until a day is picked.
    const day = dateFrom ?? dateTo;
    setDateFrom(day);
    setDateTo(day);
  };

  const handleSingleDayChange = (day: Dayjs | null) => {
    setDateFrom(day);
    setDateTo(day);
  };

  const clearFilters = () => {
    setAccountNumber("");
    setSymbol("");
    setTypeCategories([]);
    setDateFrom(null);
    setDateTo(null);
    setSingleDay(false);
  };

  const hasFilters = Boolean(
    accountNumber || symbol || typeCategories.length || dateFrom || dateTo,
  );

  const datePickerSlotProps = {
    textField: { size: "small" as const, sx: { width: 175 } },
  };

  return (
    <Box>
      <Typography variant="h6" color="primary.main" paddingX={0.5}>
        Imported Transactions
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={1.5}
        paddingX={0.5}
        paddingY={1}
      >
        <TextField
          label="Account #"
          size="small"
          value={accountNumber}
          onChange={(event) => setAccountNumber(event.target.value)}
          sx={{ width: 175 }}
        />
        <TextField
          label="Symbol"
          size="small"
          value={symbol}
          onChange={(event) => setSymbol(event.target.value)}
          sx={{ width: 140 }}
        />
        <Autocomplete
          multiple
          // Picking one category is rarely the whole question -- "buys and
          // sells" is -- so the list stays open across picks rather than
          // costing a reopen per category.
          disableCloseOnSelect
          size="small"
          options={TRANSACTION_TYPE_CATEGORIES}
          value={typeCategories}
          onChange={(_event, selected) => setTypeCategories([...selected])}
          getOptionLabel={(category) => category.label}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          // Two chips named at rest, the rest counted: the bar should say what
          // it is filtering, and "+2" does not. Two is where that stops being
          // free -- seven chips stack four rows deep and push the grid off the
          // page -- and two is also the question this filter is for ("buys and
          // sells"). The cost is that a long pair (Withdrawals + Margin
          // Interest) takes a second row in the field; the filter bar centres
          // its controls and wraps, so it absorbs that.
          limitTags={2}
          sx={{ width: 300 }}
          renderOption={(props, category, { selected }) => {
            // MUI puts the option's React key inside these props, and
            // spreading a props object that carries a key is a warning in its
            // own right -- so it is lifted out and passed directly. The five
            // other renderOption call sites in this app spread it and pass a
            // key after, which still trips the warning; do not copy them.
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ marginRight: 1 }}
                />
                {category.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Transaction Type" />
          )}
        />
        {singleDay ? (
          <DatePicker
            label="Date"
            value={dateFrom}
            onChange={handleSingleDayChange}
            format={DISPLAY_DATE_FORMAT}
            slotProps={datePickerSlotProps}
          />
        ) : (
          <>
            <DatePicker
              label="Date from"
              value={dateFrom}
              onChange={setDateFrom}
              format={DISPLAY_DATE_FORMAT}
              slotProps={datePickerSlotProps}
            />
            <DatePicker
              label="Date to"
              value={dateTo}
              onChange={setDateTo}
              format={DISPLAY_DATE_FORMAT}
              slotProps={datePickerSlotProps}
            />
          </>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={singleDay}
              onChange={(event) => handleSingleDayToggle(event.target.checked)}
            />
          }
          label="Single day"
        />
        <Button onClick={clearFilters} disabled={!hasFilters}>
          Clear filters
        </Button>
      </Stack>
      <ServerDataGrid
        // An explicit height, because this grid is filtered down to nothing
        // routinely and an unconstrained one collapses to its content: at zero
        // rows the row area became 1px tall and squashed the "No rows" overlay
        // to an invisible sliver, so a filter that matched nothing looked like
        // a page that had failed to load. A constant height also keeps the
        // pager in one place instead of moving it with every page size.
        sx={{ height: "calc(100vh - 260px)", minHeight: 420 }}
        columns={COLUMNS}
        rows={data?.results || []}
        rowCount={data?.count || 0}
        loading={isFetching}
        paginationModel={pagination}
        sortModel={sorting}
        onSortModelChange={setSorting}
        onPaginationModelChange={setPagination}
        // The bar above is the only filter surface. With filterMode "server"
        // the grid's own quick filter and filter panel would take input and
        // change nothing, which is the inert-control problem again.
        disableColumnFilter
        slotProps={{ toolbar: { showQuickFilter: false } }}
      />
    </Box>
  );
};
